// src/components/DriverAdmin.js

import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "../styles1/driverAdmin.css";

function DriverAdmin() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "drivers"), orderBy("startTime", "desc"));
      const snapshot = await getDocs(q);
      const tripsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTrips(tripsData);
    } catch (error) {
      console.error("Error fetching trips:", error);
      alert("Error loading trip data");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrip = async (tripId) => {
    if (window.confirm("Are you sure you want to delete this trip?")) {
      try {
        await deleteDoc(doc(db, "drivers", tripId));
        setTrips(trips.filter((trip) => trip.id !== tripId));
        alert("Trip deleted successfully!");
      } catch (error) {
        console.error("Error deleting trip:", error);
        alert("Error deleting trip");
      }
    }
  };

  const handleViewDetails = (trip) => {
    setSelectedTrip(trip);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTrip(null);
  };

  const calculateDistance = (trip) => {
    if (trip.endMeterReading && trip.startMeterReading) {
      return (trip.endMeterReading - trip.startMeterReading).toFixed(2);
    }
    return "N/A";
  };

  const calculateDuration = (trip) => {
    if (trip.endTime && trip.startTime) {
      const start = new Date(trip.startTime);
      const end = new Date(trip.endTime);
      const diff = end - start;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}h ${minutes}m`;
    }
    return "In Progress";
  };

  const filteredTrips = trips.filter((trip) => {
    const matchesSearch =
      trip.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trip.vehicle?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "completed" && trip.endTime) ||
      (filterStatus === "active" && !trip.endTime);

    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (trip) => {
    return trip.endTime ? (
      <span className="status-badge completed">Completed</span>
    ) : (
      <span className="status-badge active">Active</span>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner1"></div>
        <p>Loading trip data...</p>
      </div>
    );
  }

  return (
    <div className="driver-admin-container">
      <div className="admin-header1">
        <div className="header-top">
          <button className="back-btn" onClick={() => navigate(-1)}>
            Back
          </button>
          <h1>Driver Trip Management</h1>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-value">{trips.length}</div>
            <div className="stat-label">Total Trips</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {trips.filter((t) => !t.endTime).length}
            </div>
            <div className="stat-label">Active Trips</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {trips.filter((t) => t.endTime).length}
            </div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
      </div>

      <div className="filter-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by driver name or vehicle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterStatus === "all" ? "active" : ""}`}
            onClick={() => setFilterStatus("all")}
          >
            All Trips
          </button>
          <button
            className={`filter-btn ${filterStatus === "active" ? "active" : ""}`}
            onClick={() => setFilterStatus("active")}
          >
            Active
          </button>
          <button
            className={`filter-btn ${filterStatus === "completed" ? "active" : ""}`}
            onClick={() => setFilterStatus("completed")}
          >
            Completed
          </button>
        </div>
      </div>

      <div className="trips-grid">
        {filteredTrips.length === 0 ? (
          <div className="no-data">
            <p>No trips found</p>
          </div>
        ) : (
          filteredTrips.map((trip) => (
            <div key={trip.id} className="trip-card">
              <div className="trip-card-header">
                <div className="driver-info">
                  <div className="driver-avatar">
                    {trip.userName?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3>{trip.userName}</h3>
                    <p className="vehicle-name">{trip.vehicle}</p>
                  </div>
                </div>
                {getStatusBadge(trip)}
              </div>

              <div className="trip-details">
                <div className="detail-row">
                  <span className="detail-label">Start Time:</span>
                  <span className="detail-value">
                    {new Date(trip.startTime).toLocaleString()}
                  </span>
                </div>

                {trip.endTime && (
                  <div className="detail-row">
                    <span className="detail-label">End Time:</span>
                    <span className="detail-value">
                      {new Date(trip.endTime).toLocaleString()}
                    </span>
                  </div>
                )}

                <div className="detail-row">
                  <span className="detail-label">Duration:</span>
                  <span className="detail-value">
                    {calculateDuration(trip)}
                  </span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Distance:</span>
                  <span className="detail-value">
                    {calculateDistance(trip)} km
                  </span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Destinations:</span>
                  <span className="detail-value">
                    {trip.destinations?.length || 0} stops
                  </span>
                </div>
              </div>

              <div className="trip-actions">
                <button
                  className="btn-view"
                  onClick={() => handleViewDetails(trip)}
                >
                  View Details
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteTrip(trip.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && selectedTrip && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Trip Details</h2>
              <button className="close-btn" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Driver Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Driver Name:</span>
                    <span className="info-value">{selectedTrip.userName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Vehicle:</span>
                    <span className="info-value">{selectedTrip.vehicle}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Trip Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Start Time:</span>
                    <span className="info-value">
                      {new Date(selectedTrip.startTime).toLocaleString()}
                    </span>
                  </div>
                  {selectedTrip.endTime && (
                    <div className="info-item">
                      <span className="info-label">End Time:</span>
                      <span className="info-value">
                        {new Date(selectedTrip.endTime).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">Duration:</span>
                    <span className="info-value">
                      {calculateDuration(selectedTrip)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Start Reading:</span>
                    <span className="info-value">
                      {selectedTrip.startMeterReading} km
                    </span>
                  </div>
                  {selectedTrip.endMeterReading && (
                    <div className="info-item">
                      <span className="info-label">End Reading:</span>
                      <span className="info-value">
                        {selectedTrip.endMeterReading} km
                      </span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">Distance Covered:</span>
                    <span className="info-value">
                      {calculateDistance(selectedTrip)} km
                    </span>
                  </div>
                </div>
              </div>

              {selectedTrip.destinations &&
                selectedTrip.destinations.length > 0 && (
                  <div className="detail-section">
                    <h3>Destinations</h3>
                    <div className="destinations-timeline">
                      {selectedTrip.destinations.map((dest, index) => (
                        <div key={index} className="timeline-item">
                          <div className="timeline-marker">{index + 1}</div>
                          <div className="timeline-content">
                            <div className="timeline-location">
                              {dest.location}
                            </div>
                            <div className="timeline-time">
                              {new Date(dest.time).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DriverAdmin;